<template>
  <div class="content bg8">
    <div class="services-shell">
      <section class="services-hero">
        <div class="services-hero-copy">
          <div class="eyebrow">Services</div>
          <h1>Practical support for founders who need more focus, clarity, and traction.</h1>
          <p class="hero-lead">
            Foclarity is more than software. We work directly with founders, solopreneurs, and small teams to
            clarify priorities, build better operating rhythms, and turn strategy into visible progress.
          </p>
          <p class="hero-sub">
            The platform helps hold the work together. The services help you get moving faster, make better decisions,
            and stay aligned long enough to create momentum.
          </p>
          <div class="hero-actions">
            <a href="/contact" class="cta-link">
              <button class="cta-btn primary">Book a Conversation</button>
            </a>
            <router-link to="/platform" class="cta-link secondary-link">
              <button class="cta-btn secondary">See the Platform</button>
            </router-link>
          </div>
        </div>
        <div class="services-hero-card">
          <div class="signal-card">
            <div class="signal-title">Built for real founder work</div>
            <ul>
              <li>Clarify the most important priorities</li>
              <li>Build scorecards and ownership around them</li>
              <li>Create a healthier rhythm for decisions and execution</li>
              <li>Use AI where it helps, not where it distracts</li>
            </ul>
          </div>
        </div>
      </section>

      <section class="services-intro">
        <div class="section-head">
          <div class="eyebrow">What We Help With</div>
          <h2>Support that closes the gap between knowing what matters and actually following through.</h2>
          <p>
            Most small businesses do not need more complexity. They need a practical way to focus on the right work,
            assign ownership, and keep priorities visible as the business grows.
          </p>
        </div>
        <div class="services-grid">
          <article class="service-card">
            <h3>Founder Advising</h3>
            <p>
              1:1 support for early founders, solopreneurs, and owner-operators who need sharper priorities,
              better tradeoffs, and more intentional growth.
            </p>
          </article>
          <article class="service-card">
            <h3>Priority and Scorecard Design</h3>
            <p>
              We help translate your goals into a working scorecard with categories, measures, owners,
              and outcomes that your team can actually use.
            </p>
          </article>
          <article class="service-card">
            <h3>Team Alignment Support</h3>
            <p>
              For small teams that feel busy but misaligned, we create more clarity around responsibilities,
              accountability, and the work that matters most.
            </p>
          </article>
          <article class="service-card">
            <h3>AI-Assisted Execution</h3>
            <p>
              We use AI to help generate summaries, tasks, and next steps from real business context,
              so it strengthens execution instead of adding noise.
            </p>
          </article>
        </div>
      </section>

      <section class="best-fit-section">
        <div class="fit-panel copy">
          <div class="eyebrow">Best Fit</div>
          <h2>Best for businesses that have momentum, but need more structure.</h2>
          <p>
            Foclarity services are a strong fit when the challenge is not effort. It is scattered attention,
            too many moving parts, or a lack of shared clarity around what matters most.
          </p>
        </div>
        <div class="fit-panel list">
          <div class="fit-item">Early founders making high-stakes tradeoffs with limited time</div>
          <div class="fit-item">Solopreneurs who need more focus and execution discipline</div>
          <div class="fit-item">Owner-operators trying to grow without adding chaos</div>
          <div class="fit-item">Small teams that need clearer alignment and accountability</div>
        </div>
      </section>

      <section class="difference-section">
        <div class="section-head compact">
          <div class="eyebrow">Why It Works</div>
          <h2>This is not generic consulting and not just another software subscription.</h2>
        </div>
        <div class="difference-grid">
          <article class="difference-card">
            <h3>Advising + structure</h3>
            <p>
              You are not left with abstract recommendations. We help turn priorities into a system your business can
              actually operate from.
            </p>
          </article>
          <article class="difference-card">
            <h3>Software + accountability</h3>
            <p>
              The platform keeps priorities, ownership, tasks, and updates in one place so the work stays visible.
            </p>
          </article>
          <article class="difference-card">
            <h3>AI + real context</h3>
            <p>
              AI is used inside the work itself, drawing from your scorecards, tasks, and progress instead of generic prompts.
            </p>
          </article>
        </div>
      </section>

      <section class="pathways-section">
        <div class="section-head compact">
          <div class="eyebrow">Ways to Work Together</div>
          <h2>Start with the kind of support that fits where you are right now.</h2>
        </div>
        <div class="pathways-grid">
          <article class="path-card">
            <h3>Founder Advising</h3>
            <p>
              Personal support for founders who want a better thinking partner, more clarity, and stronger follow-through.
            </p>
            <router-link to="/advising" class="inline-link">Learn about advising</router-link>
          </article>
          <article class="path-card">
            <h3>Platform + System Setup</h3>
            <p>
              Build a clearer operating system for priorities, scorecards, tasks, and alignment across your team.
            </p>
            <router-link to="/platform" class="inline-link">Explore the platform</router-link>
          </article>
          <article class="path-card">
            <h3>Founder Peer Groups</h3>
            <p>
              Join a community of founders working through similar challenges with more honesty, structure, and support.
            </p>
            <router-link to="/founder-peer-groups" class="inline-link">See peer groups</router-link>
          </article>
        </div>
      </section>

      <section class="testimonial-band">
        <div class="testimonial-card">
          <div class="eyebrow">Founder Perspective</div>
          <blockquote>
            “Before Foclarity, we were swimming in an endless sea of great ideas and possibilities, but we had no
            anchor to tether them to. Foclarity helped us clarify our offerings, focus on strategic steps forward,
            and align our vision as a team.”
          </blockquote>
          <p>Allison Beck, Co-founder | Vault</p>
        </div>
      </section>

      <section class="cta-band">
        <div class="cta-copy">
          <div class="eyebrow">Next Step</div>
          <h2>Need help creating more focus in your business?</h2>
          <p>
            If you are juggling too many priorities or trying to build a better system for execution, let’s talk
            about what support would be most useful.
          </p>
        </div>
        <div class="cta-actions">
          <a href="/contact" class="cta-link">
            <button class="cta-btn primary">Start the Conversation</button>
          </a>
        </div>
      </section>
    </div>
  </div>
</template>

<script>
export default {
  title: 'Founder Advisory Services for Focus, Alignment and Execution | Foclarity',
  description: 'Foclarity services help founders and small teams clarify priorities, design scorecards, align execution, and build momentum with practical support.',
  name: 'Services',
};
</script>

<style lang="scss" scoped>
.services-shell {
  max-width: 1160px;
  margin: 0 auto;
  padding: 48px 24px 72px;
}

.eyebrow {
  color: #9a6b00;
  font-size: 0.82rem;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  margin-bottom: 14px;
}

.services-hero,
.services-intro,
.best-fit-section,
.difference-section,
.pathways-section,
.testimonial-band,
.cta-band {
  margin-bottom: 28px;
}

.services-hero {
  display: grid;
  grid-template-columns: 1.1fr 0.9fr;
  gap: 26px;
  align-items: center;
  padding: 28px 0 42px;
}

.services-hero-card,
.fit-panel,
.difference-card,
.path-card,
.testimonial-card,
.cta-band,
.service-card {
  background: #fff;
  border: 1px solid #dde5ec;
  border-radius: 28px;
  box-shadow: 0 16px 36px rgba(13, 34, 56, 0.05);
}

.services-hero-copy {
  padding: 8px 0;
}

.services-hero-copy h1,
.section-head h2,
.fit-panel h2,
.cta-copy h2 {
  color: #10263d;
  font-size: clamp(2.4rem, 4vw, 4.2rem);
  line-height: 1.06;
  margin: 0 0 18px;
}

.hero-lead,
.section-head p,
.fit-panel p,
.cta-copy p {
  color: #29435c;
  font-size: 1.08rem;
  line-height: 1.7;
  margin: 0 0 14px;
}

.hero-sub {
  color: #5b7084;
  line-height: 1.75;
  margin: 0;
}

.hero-actions,
.cta-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-top: 26px;
}

.cta-link {
  text-decoration: none;
}

.cta-btn {
  min-height: 54px;
  padding: 0 24px;
  border-radius: 999px;
  border: 1px solid transparent;
  font-size: 1rem;
  font-weight: 700;
  cursor: pointer;
  transition: transform 0.18s ease, box-shadow 0.18s ease, background 0.18s ease;
}

.cta-btn:hover {
  transform: translateY(-1px);
}

.cta-btn.primary {
  background: #123c5a;
  color: #fff;
  box-shadow: 0 16px 30px rgba(18, 60, 90, 0.18);
}

.cta-btn.secondary {
  background: #f5f8fb;
  border-color: #cfdbe5;
  color: #123c5a;
}

.services-hero-card {
  background: linear-gradient(180deg, #123c5a, #234f6e);
  border-color: #123c5a;
  padding: 22px;
  color: #fff;
  display: flex;
}

.signal-card {
  border: 1px solid rgba(255, 255, 255, 0.16);
  border-radius: 24px;
  padding: 26px;
  width: 100%;
  background: rgba(255, 255, 255, 0.05);
}

.signal-title {
  font-size: 1.4rem;
  font-weight: 700;
  margin-bottom: 16px;
}

.signal-card ul {
  margin: 0;
  padding-left: 20px;
  line-height: 1.8;
  color: rgba(255, 255, 255, 0.92);
}

.section-head {
  max-width: 780px;
  margin-bottom: 18px;
}

.section-head.compact h2 {
  font-size: clamp(1.8rem, 3vw, 2.7rem);
}

.services-grid,
.difference-grid,
.pathways-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 18px;
}

.service-card,
.difference-card,
.path-card {
  padding: 28px;
}

.service-card h3,
.difference-card h3,
.path-card h3 {
  margin: 0 0 12px;
  color: #12304b;
  font-size: 1.25rem;
}

.service-card p,
.difference-card p,
.path-card p {
  margin: 0;
  color: #5b7084;
  line-height: 1.72;
}

.best-fit-section {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 18px;
}

.fit-panel {
  padding: 28px;
}

.fit-panel.list {
  display: grid;
  gap: 12px;
  align-content: start;
}

.fit-item {
  background: #f7fafc;
  border: 1px solid #dbe6ee;
  border-radius: 18px;
  padding: 16px 18px;
  color: #24445d;
  font-weight: 600;
  line-height: 1.5;
}

.inline-link {
  display: inline-block;
  margin-top: 16px;
  color: #9a6b00;
  font-weight: 700;
  text-decoration: none;
}

.inline-link:hover {
  text-decoration: underline;
}

.testimonial-card {
  padding: 34px;
  background: linear-gradient(180deg, #123c5a, #184667);
  border-color: #123c5a;
}

.testimonial-card blockquote {
  margin: 0 0 16px;
  color: #fff;
  font-size: 1.25rem;
  line-height: 1.75;
}

.testimonial-card p {
  margin: 0;
  color: rgba(255, 255, 255, 0.86);
  font-weight: 600;
}

.cta-band {
  display: grid;
  grid-template-columns: 1.1fr 0.9fr;
  gap: 20px;
  align-items: center;
  padding: 32px 34px;
}

.secondary-link .cta-btn {
  width: 100%;
}

@media (max-width: 900px) {
  .services-hero,
  .best-fit-section,
  .cta-band,
  .services-grid,
  .difference-grid,
  .pathways-grid {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 640px) {
  .services-shell {
    padding: 28px 16px 56px;
  }

  .services-hero-card,
  .fit-panel,
  .difference-card,
  .path-card,
  .testimonial-card,
  .cta-band,
  .service-card {
    border-radius: 22px;
  }

  .service-card,
  .difference-card,
  .path-card,
  .fit-panel,
  .testimonial-card,
  .cta-band {
    padding: 24px;
  }

  .services-hero-copy {
    padding: 0;
  }

  .cta-actions,
  .hero-actions {
    flex-direction: column;
  }

  .cta-link,
  .cta-btn {
    width: 100%;
  }
}
</style>
